import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import * as uuid from "uuid"
import { HttpHeaders } from "../../../../../api/types"
import { request } from "../../../../../utils/http"
import { Setting } from "../../../../data/types"
import { PaymentGatewayEnum } from "../types"
import { GatewaySettingsFormValues } from "./types"

// Function for updating local/redux data
export const getUpdatedGatewaySettings = (
    formValues: GatewaySettingsFormValues,
    settings: Setting[],
    locationId: string
): Setting[] => {
    let updatedSettings = settings

    // Update POS gateway settings
    const posGateways = formValues.pos.gateway ? [formValues.pos.gateway] : []
    if (formValues.pos.bolt.isEnabled) {
        posGateways.push(PaymentGatewayEnum.Bolt)
    }
    updatedSettings = upsertSetting("CC_GATEWAYS", posGateways, locationId, null, updatedSettings)
    updatedSettings = upsertSetting(
        "CC_CARD_POINTE_MID",
        formValues.pos.cardpointe.MID,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting("CC_CONVERGE_MID", formValues.pos.converge.AID, locationId, null, updatedSettings)
    updatedSettings = upsertSetting(
        "CC_CONVERGE_USER_ID",
        formValues.pos.converge.UID,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting("CC_CONVERGE_PIN", formValues.pos.converge.pin, locationId, null, updatedSettings)
    updatedSettings = upsertSetting(
        "CC_UNIGATE_CUSTOMER_CODE",
        formValues.pos.unigate.customerCode,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_UNIGATE_USERNAME",
        formValues.pos.unigate.username,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_UNIGATE_PASSWORD",
        formValues.pos.unigate.password,
        locationId,
        null,
        updatedSettings
    )

    // Update Kiosk gateway settings
    const kioskGateways = formValues.kiosk.gateway ? [formValues.kiosk.gateway] : []
    if (formValues.kiosk.bolt.isEnabled) {
        kioskGateways.push(PaymentGatewayEnum.Bolt)
    }
    updatedSettings = upsertSetting("CC_KIOSK_GATEWAYS", kioskGateways, locationId, null, updatedSettings)
    updatedSettings = upsertSetting(
        "CC_CARD_POINTE_KIOSK_MID",
        formValues.kiosk.cardpointe.MID,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_CONVERGE_KIOSK_MID",
        formValues.kiosk.converge.AID,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_CONVERGE_KIOSK_USER_ID",
        formValues.kiosk.converge.UID,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_CONVERGE_KIOSK_PIN",
        formValues.kiosk.converge.pin,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_UNIGATE_KIOSK_CUSTOMER_CODE",
        formValues.kiosk.unigate.customerCode,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_UNIGATE_KIOSK_USERNAME",
        formValues.kiosk.unigate.username,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_UNIGATE_KIOSK_PASSWORD",
        formValues.kiosk.unigate.password,
        locationId,
        null,
        updatedSettings
    )

    // Update MIB gateway settings
    const mibGateways = formValues.onlineOrdering.gateway ? [formValues.onlineOrdering.gateway] : []
    updatedSettings = upsertSetting("CC_MIB_GATEWAYS", mibGateways, locationId, null, updatedSettings)
    updatedSettings = upsertSetting(
        "CC_CARD_POINTE_MIB_MID",
        formValues.onlineOrdering.cardpointe.MID,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_CONVERGE_MIB_MID",
        formValues.onlineOrdering.converge.AID,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_CONVERGE_MIB_USER_ID",
        formValues.onlineOrdering.converge.UID,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_CONVERGE_PIN_ONLINE",
        formValues.onlineOrdering.converge.pin,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_ONLINE_ORDERING_APPLE_PAY_ENABLED",
        formValues.onlineOrdering.enableApplePay,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_ONLINE_ORDERING_IS_ZIP_REQUIRED",
        formValues.onlineOrdering.isZipRequired,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_ONLINE_ORDERING_IS_CVV_REQUIRED",
        formValues.onlineOrdering.isCVVRequired,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_TSYS_MID",
        formValues.onlineOrdering.tsys.MID,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_TSYS_USER_ID",
        formValues.onlineOrdering.tsys.userId,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_TSYS_PASSWORD",
        formValues.onlineOrdering.tsys.password,
        locationId,
        null,
        updatedSettings
    )
    updatedSettings = upsertSetting(
        "CC_SEAMLESSPAY_MIB_MID",
        formValues.onlineOrdering.seamlesspay.MID,
        locationId,
        null,
        updatedSettings
    )

    // Update POS Bolt settings per terminal
    for (const terminalId in formValues.pos.bolt.devices) {
        updatedSettings = upsertSetting(
            "CC_BOLT_DEVICE_ID",
            formValues.pos.bolt.devices[terminalId]?.HSN,
            locationId,
            terminalId,
            updatedSettings
        )
        updatedSettings = upsertSetting(
            "CC_BOLT_DEVICE_AUDIO_ENABLED",
            formValues.pos.bolt.devices[terminalId]?.enableAudio,
            locationId,
            terminalId,
            updatedSettings
        )
    }

    // Update POS Dejavoo settings per terminal
    for (const terminalId in formValues.pos.dejavoo.devices) {
        updatedSettings = upsertSetting(
            "CC_DEJAVOO_AUTH_KEY",
            formValues.pos.dejavoo.devices[terminalId]?.authKey,
            locationId,
            terminalId,
            updatedSettings
        )
        updatedSettings = upsertSetting(
            "CC_DEJAVOO_REGISTER_ID",
            formValues.pos.dejavoo.devices[terminalId]?.registerId,
            locationId,
            terminalId,
            updatedSettings
        )
    }

    // Update Kiosk Bolt settings per terminal
    for (const terminalId in formValues.kiosk.bolt.devices) {
        updatedSettings = upsertSetting(
            "CC_BOLT_DEVICE_ID",
            formValues.kiosk.bolt.devices[terminalId]?.HSN,
            locationId,
            terminalId,
            updatedSettings
        )
        updatedSettings = upsertSetting(
            "CC_BOLT_DEVICE_AUDIO_ENABLED",
            formValues.kiosk.bolt.devices[terminalId]?.enableAudio,
            locationId,
            terminalId,
            updatedSettings
        )
    }

    // Update Kiosk Dejavoo settings per terminal
    for (const terminalId in formValues.kiosk.dejavoo.devices) {
        updatedSettings = upsertSetting(
            "CC_DEJAVOO_AUTH_KEY",
            formValues.kiosk.dejavoo.devices[terminalId]?.authKey,
            locationId,
            terminalId,
            updatedSettings
        )
        updatedSettings = upsertSetting(
            "CC_DEJAVOO_REGISTER_ID",
            formValues.kiosk.dejavoo.devices[terminalId]?.registerId,
            locationId,
            terminalId,
            updatedSettings
        )
    }

    // Update TSYS MIB settings per terminal
    for (const terminalId in formValues.onlineOrdering.tsys.devices) {
        updatedSettings = upsertSetting(
            "CC_TSYS_DEVICE_ID",
            formValues.onlineOrdering.tsys.devices[terminalId]?.deviceId,
            locationId,
            terminalId,
            updatedSettings
        )
    }

    return updatedSettings
}

const upsertSetting = (
    key: string,
    value: Maybe<string | boolean | number | string[]>,
    locationId: string,
    terminalId: Maybe<string>,
    settings: Setting[]
): Setting[] => {
    const settingIdx = settings.findIndex(
        setting =>
            setting.key === key &&
            setting.location_id === locationId &&
            (setting.terminal_id ?? null) === (terminalId ?? null)
    )

    // Update if setting exists
    if (settingIdx !== -1) {
        const newSettings = [...settings]

        newSettings[settingIdx] = {
            ...settings[settingIdx],
            value: formatSettingValue(value),
        }

        return newSettings
    }

    // Insert if setting does not exist
    return [
        ...settings,
        {
            id: uuid.v4(),
            key: key,
            value: formatSettingValue(value),
            location_id: locationId,
            terminal_id: terminalId,
        },
    ]
}

export const formatSettingValue = (value: Maybe<string | boolean | number | string[]>): Maybe<string> => {
    if (value == null || value === "") {
        return null
    }
    if (typeof value === "string") {
        return value
    }
    return JSON.stringify(value)
}

export const validateCredentials = async (headers: HttpHeaders, data: GatewaySettingsFormValues): Promise<void> => {
    await request({
        method: "POST",
        path: "/payment/validate-credentials",
        body: data,
        headers: headers,
    })
}

export const isWrongCredentialsError = (errorMessage: string): boolean => {
    const msg = errorMessage?.toLowerCase() ?? ""
    return msg.includes("api key is invalid") || msg.includes("api key does not match") || msg.includes("unauthorized")
}
